import type { ReactNode } from 'react';
import { usePermissions } from '@/components/ccms-components/auth/hooks/usePermissions';
import type { MemberProfileApi, MemberProfileMode } from './useMemberProfileApi';
import type { MemberRecord } from './memberFormat';

/** What the viewer may do on this profile. Mirrors the backend's [CcmsAuthorize] rules (roles OR rights). */
export interface MemberProfilePermissions {
  /** Personal, contact, addresses, health, emergency contacts */
  edit: boolean;
  /** Email, member code, type, isMember, isActive (admin only) */
  editAdmin: boolean;
  familyRead: boolean;
  familyWrite: boolean;
  familyDelete: boolean;
  campusWrite: boolean;
  notes: boolean;
  notesWrite: boolean;
  linkUser: boolean;
  planningCentre: boolean;
  anatomyView: boolean;
  anatomyManage: boolean;
  audit: boolean;
  /** Soft delete, restore and merge */
  remove: boolean;
  /** Ministries tab sections. Self mode reads the caller's own rows (api/me/*), so all are true there. */
  ministries: boolean;
  departments: boolean;
  designations: boolean;
  /** Define member custom fields (Admin or Member.CustomFields.Manage): shows the "Manage fields" link. */
  customFieldsManage: boolean;
}

export interface MemberTabProps {
  api: MemberProfileApi;
  member: MemberRecord;
  perms: MemberProfilePermissions;
}

/** A tab a host page adds to the shared profile (HR my-profile adds Employment and Files). */
export interface MemberProfileExtraTab {
  value: string;
  label: string;
  icon?: ReactNode;
  content: ReactNode;
  /** Put the tab before the member tabs instead of after them. */
  first?: boolean;
}

export function useMemberProfilePermissions(mode: MemberProfileMode): MemberProfilePermissions {
  const { hasRole, hasPermission } = usePermissions();
  if (mode === 'self') {
    return {
      edit: true,
      editAdmin: false,
      familyRead: true,
      familyWrite: false,
      familyDelete: false,
      campusWrite: true,
      notes: false,
      notesWrite: false,
      linkUser: false,
      planningCentre: false,
      anatomyView: false,
      anatomyManage: false,
      audit: false,
      remove: false,
      ministries: true,
      departments: true,
      designations: true,
      customFieldsManage: false,
    };
  }
  const admin = hasRole('Admin');
  const any = (...rights: string[]) => admin || rights.some((r) => hasPermission(r));
  return {
    edit: any('Member.Write'),
    editAdmin: any('Member.Write'),
    familyRead: any('Member.Read', 'Member.Family.Read'),
    familyWrite: any('Member.Family.Write'),
    familyDelete: any('Member.Family.Delete'),
    campusWrite: any('Member.Write', 'Member.Location.Write'),
    notes: any('Member.Read'),
    notesWrite: any('Member.Write'),
    linkUser: any('Member.Write'),
    // The Planning Centre routes accept Member.Write (or the never-seeded Member.Update).
    planningCentre: any('Member.Write', 'Member.Update'),
    anatomyView: admin || hasRole('AnatomyAccess') || hasPermission('Anatomy.View'),
    anatomyManage: admin || hasRole('AnatomyAccess') || hasPermission('Anatomy.Manage'),
    audit: any('Member.Read'),
    remove: any('Member.Delete'),
    ministries: any('Ministry', 'Member.Department'),
    departments: any('Member.Department'),
    designations: any('Member.Designation'),
    customFieldsManage: any('Member.CustomFields.Manage'),
  };
}
